import type { ConsentSnapshot } from '@/Integrations/Consent/types';
import {
  createDefaultSnapshot,
  createSnapshot,
  isRecord,
} from '@/Integrations/Consent/providers/nativeConsent/shared';

type ConsentmanagerApi = (
  command: string,
  parameter?: unknown,
  callback?: unknown,
  async?: boolean,
) => unknown;

type ConsentmanagerEventHandler = (
  eventName: string,
  cmpObject?: unknown,
  subtype?: unknown,
) => void;

const CONSENTMANAGER_GOOGLE_ANALYTICS_VENDOR_ID = 's26';
const CONSENTMANAGER_GOOGLE_TAG_MANAGER_VENDOR_ID = 's905';

function getConsentmanagerApi(): ConsentmanagerApi | undefined {
  // eslint-disable-next-line no-underscore-dangle
  return (window as unknown as { __cmp?: unknown }).__cmp as ConsentmanagerApi | undefined;
}

function readConsentmanagerCommand(command: string): Record<string, unknown> | null {
  const cmpApi = getConsentmanagerApi();
  if (typeof cmpApi !== 'function') {
    return null;
  }

  try {
    const result = cmpApi(command);
    return isRecord(result) ? result : null;
  } catch {
    return null;
  }
}

function createConsentmanagerSignature(snapshot: ConsentSnapshot): string {
  const raw = isRecord(snapshot.raw) ? snapshot.raw : {};
  const consentStatus = isRecord(raw.consentStatus) ? raw.consentStatus : {};
  const cmpData = isRecord(raw.cmpData) ? raw.cmpData : {};

  return JSON.stringify({
    functional: snapshot.functional,
    analytics: snapshot.analytics,
    marketing: snapshot.marketing,
    source: snapshot.source,
    consentData: consentStatus.consentData,
    consentstring: cmpData.consentstring,
  });
}

function addConsentmanagerEventListener(
  eventName: string,
  handler: ConsentmanagerEventHandler,
): (() => void) | null {
  const cmpApi = getConsentmanagerApi();
  if (typeof cmpApi !== 'function') {
    return null;
  }

  try {
    cmpApi('addEventListener', [eventName, handler, false], null);
    return () => {
      cmpApi('removeEventListener', [eventName, handler, false], null);
    };
  } catch {
    return null;
  }
}

function isGranted(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    value === 3 ||
    value === '1' ||
    value === '3' ||
    value === 'true'
  );
}

function hasMappedConsent(consents: unknown, vendorId: string): boolean {
  if (!isRecord(consents)) {
    return false;
  }

  return isGranted(consents[vendorId]);
}

function hasConsentmanagerAnalyticsConsent(cmpData: Record<string, unknown>): boolean {
  return hasMappedConsent(cmpData.vendorConsents, CONSENTMANAGER_GOOGLE_ANALYTICS_VENDOR_ID);
}

function hasConsentmanagerMarketingConsent(cmpData: Record<string, unknown>): boolean {
  return hasMappedConsent(cmpData.vendorConsents, CONSENTMANAGER_GOOGLE_TAG_MANAGER_VENDOR_ID);
}

export function readConsentmanagerSnapshot(
  overrides: Partial<Pick<ConsentSnapshot, 'analytics' | 'marketing'>> = {},
): ConsentSnapshot {
  const consentStatus = readConsentmanagerCommand('consentStatus');
  const cmpData = readConsentmanagerCommand('getCMPData');

  if (!consentStatus && !cmpData) {
    return createDefaultSnapshot();
  }

  if (cmpData?.gdprApplies === false || cmpData?.regulation === 0) {
    return createSnapshot({ analytics: true, marketing: true }, cmpData);
  }

  if (consentStatus?.consentExists === false) {
    return createSnapshot({ analytics: false, marketing: false }, { consentStatus, cmpData });
  }

  const analyticsConsent =
    overrides.analytics ?? (cmpData ? hasConsentmanagerAnalyticsConsent(cmpData) : false);
  const marketingConsent =
    overrides.marketing ?? (cmpData ? hasConsentmanagerMarketingConsent(cmpData) : false);

  return createSnapshot(
    {
      analytics: analyticsConsent,
      marketing: marketingConsent,
    },
    { consentStatus, cmpData },
  );
}

export function watchConsentmanagerConsent(
  onSnapshot: (snapshot: ConsentSnapshot) => void,
): () => void {
  let previousSignature = '';
  let overrides: Partial<Pick<ConsentSnapshot, 'analytics' | 'marketing'>> = {};
  const cleanupListeners: Array<() => void> = [];

  const publish = (force = false) => {
    const snapshot = readConsentmanagerSnapshot(overrides);
    const signature = createConsentmanagerSignature(snapshot);

    if (!force && signature === previousSignature) {
      return;
    }

    previousSignature = signature;
    onSnapshot(snapshot);
  };

  publish();

  const eventOverrides: Record<
    string,
    Partial<Pick<ConsentSnapshot, 'analytics' | 'marketing'>> | null
  > = {
    consent: null,
    consentapproved: { analytics: true, marketing: true },
    consentrejected: { analytics: false, marketing: false },
    consentcustom: null,
  };

  Object.entries(eventOverrides).forEach(([eventName, nextOverrides]) => {
    const cleanup = addConsentmanagerEventListener(eventName, () => {
      overrides = nextOverrides ?? {};
      publish(true);
    });

    if (cleanup) {
      cleanupListeners.push(cleanup);
    }
  });

  const intervalId = window.setInterval(publish, 1000);

  return () => {
    window.clearInterval(intervalId);
    cleanupListeners.forEach((cleanup) => cleanup());
  };
}
